import { useState } from "react";
import { aiGenerateBlog } from "../../lib/api";
import { Sparkles, Loader2, X } from "lucide-react";

export default function AIBlogGenerator({ onComplete, onClose }) {
  const [topic, setTopic] = useState("");
  const [keywords, setKeywords] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const generate = async (e) => {
    e?.preventDefault();
    if (!topic.trim()) return;
    setLoading(true); setError("");
    try {
      const draft = await aiGenerateBlog(topic, keywords, category);
      onComplete(draft);
    } catch (err) {
      setError(err?.response?.data?.detail || "Generation failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="ai-generator-overlay">
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={generate}
        className="bg-white w-full max-w-lg rounded-2xl border border-black/5 shadow-2xl overflow-hidden"
        data-testid="ai-generator-modal"
      >
        <div className="bg-forest text-bone p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-terracotta grid place-items-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-display font-bold text-xl">AI Blog Generator</h3>
              <p className="text-xs text-bone/70">Powered by Claude Sonnet 4.5</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-bone/70 hover:text-bone p-1" data-testid="ai-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="text-sm font-semibold text-charcoal mb-1.5 block">Topic *</label>
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g., What to do if you're rear-ended on I-71"
              className="w-full p-3 rounded-lg border border-black/10 focus:border-forest outline-none"
              required
              data-testid="ai-topic-input"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-charcoal mb-1.5 block">Target Keywords</label>
            <input
              type="text"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder="e.g., Ohio rear-end accident, whiplash claim"
              className="w-full p-3 rounded-lg border border-black/10 focus:border-forest outline-none"
              data-testid="ai-keywords-input"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-charcoal mb-1.5 block">Category (optional)</label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g., Car Accidents"
              className="w-full p-3 rounded-lg border border-black/10 focus:border-forest outline-none"
              data-testid="ai-category-input"
            />
          </div>

          {error && <p className="text-red-600 text-sm" data-testid="ai-error">{String(error)}</p>}

          <div className="bg-bone-alt rounded-lg p-3 text-xs text-charcoal-soft">
            <strong>Note:</strong> Drafts are unpublished by default. You'll review and approve before publishing.
            Generation takes 15-30 seconds.
          </div>
        </div>

        <div className="border-t border-black/5 p-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-ghost text-sm" data-testid="ai-cancel">
            Cancel
          </button>
          <button type="submit" disabled={loading || !topic.trim()} className="btn-secondary text-sm disabled:opacity-50" data-testid="ai-generate">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><Sparkles className="w-4 h-4" /> Generate draft</>}
          </button>
        </div>
      </form>
    </div>
  );
}
